import { useEffect, useState } from 'react';
import { Check, ChevronDown, Pencil, Plane, Plus, User, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useServices } from '../../app/services';
import { currencyExponent } from '../../domain/money';
import type { BudgetSpace, BudgetType } from '../../domain/model';
import { parseAmount } from '../../i18n/parseAmount';
import { CURRENCIES, currencyName } from '../../i18n/regions';
import { BottomSheet, Button, ChoiceList, IconButton, SelectField, TextField, useToast } from '../../ui';
import './spaces.css';

const TYPE_ICON = { personal: User, household: Users, other: Plane } as const;

/** Every budget the user is in, oldest first, refreshed when data changes. */
export function useBudgets() {
  const { store, identity, dataVersion, activeSpace } = useServices();
  const [budgets, setBudgets] = useState<BudgetSpace[] | null>(null);
  useEffect(() => {
    let live = true;
    void store.spaces.listForUser(identity.session.userId).then((list) => live && setBudgets(list));
    return () => {
      live = false;
    };
  }, [store, identity, dataVersion, activeSpace]);
  return budgets;
}

/**
 * The header pill: the budget you are in. Tapping it opens the switcher.
 * Everything below (entries, categories, bills, analytics) belongs to it.
 */
export function BudgetPill() {
  const { t } = useTranslation();
  const { activeSpace } = useServices();
  const [open, setOpen] = useState(false);
  if (!activeSpace) return null;
  return (
    <>
      <button
        type="button"
        className="pill-hit"
        data-testid="active-budget"
        aria-haspopup="dialog"
        aria-label={t('spaces.pillLabel', { name: activeSpace.name })}
        onClick={() => setOpen(true)}
      >
        <span className="pill pill-button">
          {activeSpace.name}
          <ChevronDown size={14} aria-hidden />
        </span>
      </button>
      <BudgetSwitcher open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/**
 * Switch budget, or add one. Each budget keeps its own members, categories,
 * currency, entries and analytics; switching only changes which one the app
 * shows and where new entries go.
 */
export function BudgetSwitcher({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { activeSpace, setActiveSpace } = useServices();
  const toast = useToast();
  const budgets = useBudgets();
  const [form, setForm] = useState<{ space: BudgetSpace | null } | null>(null);

  async function choose(space: BudgetSpace) {
    onClose();
    if (space.id === activeSpace?.id) return;
    await setActiveSpace(space);
    toast.show({ message: t('spaces.switched', { name: space.name }) });
  }

  return (
    <>
      <BottomSheet
        open={open && !form}
        onClose={onClose}
        title={t('spaces.title')}
        footer={
          <Button variant="secondary" size="lg" block icon={Plus} onClick={() => setForm({ space: null })}>
            {t('spaces.new')}
          </Button>
        }
      >
        <ul className="space-list" aria-label={t('spaces.title')}>
          {(budgets ?? []).map((b) => {
            const Icon = TYPE_ICON[b.type];
            const current = b.id === activeSpace?.id;
            return (
              <li key={b.id} className={current ? 'space-item is-current' : 'space-item'}>
                <button type="button" className="space-choose" aria-current={current || undefined} onClick={() => void choose(b)}>
                  <span className="space-icon" aria-hidden>
                    <Icon size={20} />
                  </span>
                  <span className="space-text">
                    <span className="space-name">{b.name}</span>
                    <span className="space-meta">
                      {t(`spaces.type.${b.type}`)} · {b.currency}
                    </span>
                  </span>
                  {current && <Check className="space-check" size={20} aria-label={t('spaces.current')} />}
                </button>
                <IconButton icon={Pencil} label={t('spaces.edit', { name: b.name })} onClick={() => setForm({ space: b })} />
              </li>
            );
          })}
        </ul>
      </BottomSheet>
      <BudgetForm
        open={open && Boolean(form)}
        space={form?.space ?? null}
        onClose={() => setForm(null)}
        onDone={() => {
          setForm(null);
          onClose();
        }}
      />
    </>
  );
}

/** New budget (name, type, currency, optional monthly target) or edit one (currency stays: its entries are in it). */
function BudgetForm({ open, space, onClose, onDone }: { open: boolean; space: BudgetSpace | null; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const { activeSpace, language, createSpace, updateSpace } = useServices();
  const toast = useToast();
  const [name, setName] = useState('');
  const [type, setType] = useState<BudgetType>('other');
  const [currency, setCurrency] = useState('USD');
  const [target, setTarget] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(space?.name ?? '');
    setType(space?.type ?? 'other');
    setCurrency(space?.currency ?? activeSpace?.currency ?? 'USD');
    setTarget(
      space?.monthlyTargetMinor ? (space.monthlyTargetMinor / 10 ** currencyExponent(space.currency)).toFixed(currencyExponent(space.currency)) : '',
    );
    setTouched(false);
    setFailed(false);
  }, [open, space, activeSpace]);

  const parsedTarget = target.trim() ? parseAmount(target, currency) : null;
  const nameError = touched && !name.trim() ? t('spaces.nameRequired') : undefined;
  const targetError = parsedTarget === 'invalid' || parsedTarget === 0 ? t('quickAdd.invalid') : undefined;

  async function save() {
    setTouched(true);
    if (!name.trim() || targetError || busy) return;
    const monthlyTargetMinor = typeof parsedTarget === 'number' ? parsedTarget : null;
    setBusy(true);
    setFailed(false);
    try {
      if (space) {
        await updateSpace(space.id, { name: name.trim(), type, monthlyTargetMinor });
        toast.show({ message: t('spaces.saved', { name: name.trim() }) });
      } else {
        const created = await createSpace({ name: name.trim(), type, currency, monthlyTargetMinor });
        toast.show({ message: t('spaces.created', { name: created.name }) });
      }
      onDone();
    } catch (e) {
      console.error('[spaces]', e);
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={space ? t('spaces.editTitle') : t('spaces.new')}
      footer={
        <Button variant="primary" size="lg" block loading={busy} onClick={() => void save()}>
          {space ? t('spaces.save') : t('spaces.create')}
        </Button>
      }
    >
      <div className="stack">
        <TextField label={t('spaces.name')} value={name} maxLength={60} placeholder={t('spaces.namePlaceholder')} onChange={(e) => setName(e.target.value)} error={nameError} />
        <div className="stack stack-xs">
          <p className="field-label">{t('spaces.typeLabel')}</p>
          <ChoiceList<BudgetType>
            label={t('spaces.typeLabel')}
            value={type}
            onChange={setType}
            options={(['personal', 'household', 'other'] as const).map((v) => {
              const Icon = TYPE_ICON[v];
              return { value: v, label: t(`spaces.type.${v}`), description: t(`spaces.typeBody.${v}`), leading: <Icon size={22} aria-hidden /> };
            })}
          />
        </div>
        {space ? (
          <p className="field-msg">{t('spaces.currencyFixed', { currency: `${currencyName(space.currency, language)} (${space.currency})` })}</p>
        ) : (
          <SelectField label={t('spaces.currency')} value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {currencyName(c, language)} ({c})
              </option>
            ))}
          </SelectField>
        )}
        <TextField
          label={t('spaces.target')}
          hint={t('spaces.targetHint')}
          inputMode="decimal"
          autoComplete="off"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          error={targetError}
        />
        {failed && (
          <p className="field-msg field-error" role="alert">
            {t('spaces.error')}
          </p>
        )}
      </div>
    </BottomSheet>
  );
}
